// Shared "Sync now" helper. Mirrors the toast handling on /ln so that the
// compact sync card on Uploads + Settings behaves identically.
import { toast } from "sonner";
import { Capacitor } from "@capacitor/core";
import { drainPingOutbox } from "./ping-outbox";
import { drainNativeLocationQueue, LocationBG, locationBGRegistered, getNativeQueueStats } from "./location-bg";
import { getCachedTripSync, hydrateCachedTrip } from "@/lib/offline/trip-cache";
import { resumeSyncFlow } from "@/lib/auth/resume-sync";

export type RunSyncOptions = {
  /** Router navigate fn (passed through to resumeSyncFlow if needed). */
  navigate?: (path: string) => void;
  /** Which surface triggered the sync. Tunes toast wording so the user
   *  doesn't get told to "tap Queue" while already on the Tracking page. */
  source?: "tracking" | "media" | "default";
};

let inFlight = false;
let inFlightStartedAt = 0;

async function resolveTripId(): Promise<string | null> {
  const sync = getCachedTripSync()?.id ?? null;
  if (sync) return sync;
  try {
    const t = await hydrateCachedTrip();
    return t?.id ?? null;
  } catch {
    return null;
  }
}

function emitDrainComplete(detail: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent("mtsync:drain-complete", { detail }));
  } catch { /* ignore */ }
}

/** Purge any points the native side has already marked delivered so the
 * visible On-device queue count drops after a successful sync. OTA-safe —
 * uses the existing clearDeliveredLocationPoints method on the installed
 * LocationBG plugin. No-op on web / unsupported builds. */
async function purgeNativeDelivered(): Promise<number> {
  if (!Capacitor.isNativePlatform() || !locationBGRegistered()) return 0;
  try {
    const r = await LocationBG.clearDeliveredLocationPoints();
    return (r as any)?.removed ?? 0;
  } catch (e) {
    console.warn("[run-sync] clearDeliveredLocationPoints failed", e);
    return 0;
  }
}

const STILL_RUNNING_MS = 25_000;

export async function runSyncNow(opts: RunSyncOptions = {}): Promise<void> {
  if (inFlight) {
    if (opts.source === "tracking") {
      toast("Sync is already running. Counts may rise while tracking records new points — they'll drop as the server confirms each batch.");
    } else if (opts.source === "media") {
      toast("Upload already running — give it a moment.");
    } else {
      toast("Sync already running — give it a moment.");
    }
    return;
  }

  inFlight = true;
  inFlightStartedAt = Date.now();

  // Snapshot native queue size BEFORE drain so we can report accurate counts
  // even when drainNativeLocationQueue dedupes within-batch.
  const beforeStats = await getNativeQueueStats().catch(() => null);
  const beforeNative = beforeStats?.native_queue_count ?? 0;

  const runningToast = toast.loading(
    beforeNative > 0 ? `Syncing ${beforeNative.toLocaleString()} on-device point${beforeNative === 1 ? "" : "s"}…` : "Syncing…",
  );
  emitDrainComplete({ reason: "manual-sync", phase: "start" });

  // If the drain takes longer than STILL_RUNNING_MS, downgrade the toast so
  // the UI stops looking blocked. The drain itself keeps running.
  let resolved = false;
  const stillRunningTimer = window.setTimeout(() => {
    if (resolved) return;
    toast.dismiss(runningToast);
    toast("Still syncing in the background — large queues take a while");
  }, STILL_RUNNING_MS);

  try {
    // Step 1: drain the native (Swift) LocationBG queue into the JS outbox.
    const tripId = await resolveTripId();
    let nativeDrained = 0;
    try {
      const r = await drainNativeLocationQueue(tripId);
      nativeDrained = r?.drained ?? 0;
    } catch (e) {
      console.warn("[run-sync] drainNativeLocationQueue failed", e);
    }

    // Step 2: drain the JS outbox to the server.
    const res = await drainPingOutbox({ userInitiated: true });

    // Step 3: purge delivered points from the native buffer so the on-device
    // queue count actually drops in the UI. OTA-safe — uses the existing
    // clearDeliveredLocationPoints method.
    await purgeNativeDelivered();
    const afterStats = await getNativeQueueStats().catch(() => null);
    const afterNative = afterStats?.native_queue_count ?? 0;
    const nativeCleared = Math.max(0, beforeNative - afterNative);
    const remaining = afterNative;

    resolved = true;
    window.clearTimeout(stillRunningTimer);
    toast.dismiss(runningToast);

    const tail = remaining > 0 ? ` · ${remaining.toLocaleString()} still queued` : "";

    if (res.status === "ok") {
      const total = Math.max(res.synced, nativeDrained, nativeCleared);
      if (total > 0) {
        toast.success(`Synced ${total.toLocaleString()} point${total === 1 ? "" : "s"}${tail}`);
      } else {
        toast("Already synced");
      }
    } else if (res.status === "empty") {
      if (nativeDrained > 0 || nativeCleared > 0) {
        const total = Math.max(nativeDrained, nativeCleared);
        toast.success(`Synced ${total.toLocaleString()} point${total === 1 ? "" : "s"}${tail}`);
      } else if (beforeNative > 0 && afterNative === beforeNative) {
        // Native queue has points but none were drainable (e.g. no trip
        // context). Be honest instead of saying "Nothing to sync".
        toast(`${beforeNative.toLocaleString()} on-device point${beforeNative === 1 ? "" : "s"} waiting — open the active trip to sync.`);
      } else {
        toast("Nothing to sync");
      }
    } else if (res.status === "busy") {
      toast("Sync already in progress");
    } else if (res.status === "unreachable") {
      toast.error("Can't reach server", {
        description: `Check your connection and try again.${tail ? ` ${remaining.toLocaleString()} point${remaining === 1 ? "" : "s"} still queued locally.` : ""}`,
      });
    } else if (res.status === "error") {
      toast.error("Sync failed", { description: res.error });
    } else if (res.status === "needs_login") {
      toast.error("Sign in to resume sync", {
        description: "Your queued points are safe — they'll send after you sign in.",
        action: {
          label: "Sign in",
          onClick: () => {
            void resumeSyncFlow((path) => {
              if (opts.navigate) opts.navigate(path);
              else if (typeof window !== "undefined") window.location.assign(path);
            });
          },
        },
      });
    }
  } finally {
    resolved = true;
    window.clearTimeout(stillRunningTimer);
    inFlight = false;
    emitDrainComplete({ reason: "manual-sync", phase: "complete" });
  }
}

